/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { Avatar, Button, Divider, Pagination, Stack, Typography, useTheme } from '@mui/material';
import Link from 'next/link';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmDialog from '../../components/ConfirmDialog';
import EmptyContent from '../../components/EmptyContent';
import Iconify from '../../components/Iconify';
import Markdown from '../../components/Markdown';
import { SkeletonCommentItem } from '../../components/skeleton/SkeletonItem';
import useAuth from '../../hooks/useAuth';
import useLocales from '../../hooks/useLocales';
import { setCommentSearch } from '../../redux/slices/blog/blog.comment';
import { deleteCommentAPI, findCommentAPI, updateLikeAPI } from '../../service/blog/blog.comment.service';
import { fShortenNumber } from '../../utils/formatNumber';
import CommentNewForm from './CommentNewForm';

// ----------------------------------------------------------------------
export function CommentItemWrapper({ comment }) {
  const { translate } = useLocales();

  let [isShowReplies, setShowReplies] = useState(false);

  let [search, setSearch] = useState({
    page: 0,
    size: 10,
    value: '%%',
    orders: [{ order: "desc", property: 'createdAt' }],
    filterBys: { itemId: comment?.id, type: "REPLY" }
  });

  let [response, setResponse] = useState({
    isLoading: false,
    totalPages: 0,
    numberOfElements: 0,
    comments: [],
  });

  useEffect(() => {
    isShowReplies && loadReplies();
  }, [search, isShowReplies, comment?.replyNo]);

  const loadReplies = async () => {
    setResponse({ ...response, isLoading: true });

    const { code, data: comments, totalPages, numberOfElements } = await findCommentAPI(search);

    if (code === '200')
      setResponse({ ...response, comments, totalPages, numberOfElements, isLoading: false });
    else
      setResponse({ ...response, isLoading: false });
  };

  const handleShowReplies = async () => {
    setShowReplies(!isShowReplies);
    setSearch({ ...search, page: 0 });
  };

  const handleChangePage = (_, value) => {
    setSearch({ ...search, page: value - 1 });
  };

  return <>
    <CommentItem comment={comment} showReplies={handleShowReplies} />

    {isShowReplies && (
      <Stack spacing={2} pl={5}>
        <Stack spacing={2} >
          {response.comments.map((c) => (
            <ReplyItem comment={c} key={c.id} />
          ))}
          {response.isLoading ? <SkeletonCommentItem /> :
            response.numberOfElements == 0 && <EmptyContent title={" "} description={translate("label.noComment")} />}
        </Stack>
        {response.numberOfElements > 0 && <Stack alignItems={"center"} my={1.5}>
          <Pagination count={response.totalPages} variant='text' page={search.page + 1} onChange={handleChangePage} />
        </Stack>}
      </Stack>
    )}
  </>;
}

export function CommentItem({ comment, showReplies }) {
  const { translate } = useLocales();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [openReply, setOpenReply] = useState(false);
  const [likeNo, setLikeNo] = useState(comment?.likeNo);

  const handleLike = async () => {
    if (!isAuthenticated) {
      enqueueSnackbar(translate("label.signinToGPLX"), { variant: 'error' });
      return;
    }
    setLikeNo(likeNo + 1);
    updateLikeAPI(comment?.id);
  };

  return (
    <Stack spacing={1}>
      <CommentContent comment={comment} />

      <Stack direction="row">
        <Button color='inherit' variant='text' startIcon={<Iconify icon="iconamoon:like-light" sx={{ height: 18, width: 18 }} />} sx={{ ...theme.typography.body2 }}
          onClick={handleLike}>
          {fShortenNumber(likeNo)}
        </Button>
        <Button color='inherit' variant='text' startIcon={<Iconify icon="humbleicons:chat" sx={{ height: 18, width: 18 }} />} sx={{ ...theme.typography.body2 }}
          onClick={showReplies}>
          {fShortenNumber(comment?.replyNo)}
        </Button>
        <Button color='inherit' variant='text' startIcon={<Iconify icon="bi:reply" sx={{ height: 18, width: 18 }} />} sx={{ ...theme.typography.body2 }}
          onClick={() => setOpenReply(!openReply)}>
          {translate("button.reply")}
        </Button>
      </Stack>

      {openReply && (<CommentNewForm itemId={comment?.id} type="REPLY" />)}
      <Divider />
    </Stack >
  );
}

const ReplyItem = ({ comment }) => (
  <Stack spacing={1}>
    <CommentContent comment={comment} />
    <Divider />
  </Stack >
);

const CommentContent = ({ comment }) => {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const { search } = useSelector((state) => state.blogComment);
  const [open, setOpen] = useState(false);
  const [editable, setEditable] = useState(false);

  const confirmDeleteItem = async () => {
    let resp = await deleteCommentAPI(comment?.id);

    setOpen(false);
    if (resp.code === "200") {
      enqueueSnackbar(translate('message.deleteSuccess'), { variant: 'success' });
      dispatch(setCommentSearch({ ...search }));
    } else
      enqueueSnackbar(`${resp.code} - ${resp.message}`, { variant: 'error' });
  };

  const handleEdit = async () => {
    setEditable(true);
  };

  return (<>
    <Stack direction={"row"} spacing={1} justifyContent={"space-between"}>
      <Stack direction={"row"} spacing={1} >
        <Link href={`/user/${comment?.createdBy?.id}`} passHref>
          <Avatar alt={comment?.createdBy?.displayName} src={comment?.createdBy?.photoURL} sx={{ width: 36, height: 36, cursor: 'pointer' }} />
        </Link>
        <Stack>
          <Link href={`/user/${comment?.createdBy?.id}`} passHref>
            <Typography variant="subtitle2" sx={{ cursor: 'pointer' }}>{comment?.createdBy?.displayName}</Typography>
          </Link>
          <Typography variant="caption" sx={{ color: 'text.disabled', }} >{comment?.createdAt}</Typography>
        </Stack>
      </Stack>
      <Stack direction={"row"} spacing={1} >
        {user?.id === comment?.createdBy.id && (
          <Button onClick={handleEdit} size='small' color='primary' variant='text' startIcon={<Iconify icon="circum:edit" sx={{ height: 18, width: 18 }} />}>
            {translate("button.edit")}
          </Button>)}
        {user?.id === comment?.createdBy.id && (
          <Button size='small' color='error' variant='text' startIcon={<Iconify icon="octicon:trash-16" sx={{ height: 18, width: 18 }} />}
            onClick={() => setOpen(true)}>
            {translate("button.delete")}
          </Button>)}
      </Stack>
    </Stack>
    {editable ? <CommentNewForm id={comment?.id} content={comment?.description} isEdit={true} setEditable={setEditable} /> :
      <div className='ql-snow'>
        <div className='ql-editor' style={{ padding: 0 }}>
          <Markdown children={comment?.description} />
        </div>
      </div>}
    <ConfirmDialog values={{ title: translate("message.dialogDeleteTitle"), content: translate("message.dialogDeleteContent") }}
      onClose={() => setOpen(false)} isOpen={open} onSubmit={confirmDeleteItem} />
  </>);
};